import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, Trash2, Loader2, Save, Plus, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { errMsg, formatDate, BACKEND_URL } from "@/lib/api";
import { useAdminApi } from "@/admin/useAdminApi";
import { Loading } from "@/components/site/Ui";

function useLoad(path) {
  const call = useAdminApi();
  const [data, setData] = useState(null);
  const reload = () => call("get", path).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { reload(); }, [path]); // eslint-disable-line react-hooks/exhaustive-deps
  return [data, reload, call];
}

const Stat = ({ label, value, to, sub, testId }) => (
  <Link to={to} className="card card-hover p-5" data-testid={testId}>
    <p className="text-xs font-semibold uppercase tracking-wider text-[#8A96A8]">{label}</p>
    <p className="mt-2 font-display text-3xl font-bold text-[#17243A]">{value}</p>
    {sub && <p className="mt-1 text-xs text-[#64728A]">{sub}</p>}
  </Link>
);

export function AdminHome() {
  const [s] = useLoad("/admin/stats");
  if (!s) return <Loading />;
  const sub = (x) => `${x.published} live · ${x.draft} drafts · ${x.scheduled} scheduled`;
  return (
    <div className="space-y-8" data-testid="admin-home">
      <h1 className="text-3xl font-bold">Dashboard</h1>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Stat label="Software listings" value={s.software.total} sub={sub(s.software)} to="/admin/software" testId="admin-stat-software" />
        <Stat label="Articles" value={s.articles.total} sub={sub(s.articles)} to="/admin/articles" testId="admin-stat-articles" />
        <Stat label="Comparisons" value={s.comparisons.total} sub={sub(s.comparisons)} to="/admin/comparisons" testId="admin-stat-comparisons" />
        <Stat label="Newsletter subscribers" value={s.subscribers} to="/admin/subscribers" testId="admin-stat-subscribers" />
        <Stat label="New messages" value={s.messages_new} to="/admin/messages" testId="admin-stat-messages" />
      </div>
      {(s.needs_review > 0 || s.pages_unreviewed > 0) && (
        <div className="card p-6 border-amber-200 bg-amber-50/50" data-testid="admin-launch-checklist">
          <h2 className="text-lg font-semibold flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-amber-600" />Before launch</h2>
          <ul className="mt-3 space-y-2 text-sm">
            {s.needs_review > 0 && <li><Link to="/admin/software" className="text-[#3F5E7A] underline">{s.needs_review} software listings</Link> still need your accuracy review (starter listings contain factual basics only).</li>}
            {s.pages_unreviewed > 0 && <li><Link to="/admin/pages" className="text-[#3F5E7A] underline">{s.pages_unreviewed} legal pages</Link> are templates awaiting owner/legal review.</li>}
            <li>Configure your contact mailbox, Google Analytics 4 and Search Console in <Link to="/admin/settings" className="text-[#3F5E7A] underline">Settings</Link>.</li>
          </ul>
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        <Link to="/admin/articles/new" className="btn-primary" data-testid="admin-quick-article"><Plus className="h-4 w-4" />New article</Link>
        <Link to="/admin/software/new" className="btn-secondary" data-testid="admin-quick-software"><Plus className="h-4 w-4" />New software</Link>
        <Link to="/admin/comparisons/new" className="btn-secondary" data-testid="admin-quick-comparison"><Plus className="h-4 w-4" />New comparison</Link>
      </div>
    </div>
  );
}

export function Subscribers() {
  const [rows, reload, call] = useLoad("/admin/subscribers");
  const exportCsv = async () => {
    try {
      const r = await call("get", "/admin/subscribers.csv", null, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = Object.assign(document.createElement("a"), { href: url, download: "saaspivora-subscribers.csv" });
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (s) => { if (window.confirm(`Remove ${s.email}?`)) { await call("delete", `/admin/subscribers/${s.id}`); reload(); } };
  return (
    <div data-testid="admin-subscribers">
      <div className="flex justify-between items-center mb-2"><h1 className="text-3xl font-bold">Subscribers</h1>
        <button onClick={exportCsv} className="btn-primary" data-testid="admin-export-csv"><Download className="h-4 w-4" />Export CSV</button></div>
      <p className="text-sm text-[#64728A] mb-6">Subscribers are stored with their consent record. Export the CSV to import into your mailing service.</p>
      {!rows ? <Loading /> : (
        <div className="card overflow-x-auto"><table className="w-full text-sm min-w-[480px]">
          <thead><tr className="bg-[#F1F4F6] text-left text-xs uppercase tracking-wider text-[#64728A]"><th className="p-3 pl-5">Email</th><th className="p-3">Consent</th><th className="p-3">Subscribed</th><th className="p-3" /></tr></thead>
          <tbody>{rows.map((s) => (
            <tr key={s.id} className="border-t border-[#E7EDEF]"><td className="p-3 pl-5 font-medium text-[#17243A]">{s.email}</td><td className="p-3">{s.consent ? "Yes" : "No"}</td>
              <td className="p-3 text-[#64728A]">{formatDate(s.created_at)}</td>
              <td className="p-3 text-right"><button onClick={() => del(s)} className="btn-ghost !p-2 text-rose-600" aria-label="Remove" data-testid={`admin-sub-delete-${s.id}`}><Trash2 className="h-4 w-4" /></button></td></tr>
          ))}{!rows.length && <tr><td colSpan={4} className="p-10 text-center text-[#8A96A8]">No subscribers yet</td></tr>}</tbody>
        </table></div>
      )}
    </div>
  );
}

const EMAIL_BADGE = { sent: "bg-[#ECF6F4] text-[#0F766E]", failed: "bg-rose-50 text-rose-700", pending: "bg-[#E7EDEF]", skipped: "bg-[#E7EDEF]" };

export function Messages() {
  const [rows, reload, call] = useLoad("/admin/messages");
  const [open, setOpen] = useState(null);
  const setStatus = async (m, status) => { await call("patch", `/admin/messages/${m.id}`, { status }); reload(); };
  return (
    <div data-testid="admin-messages">
      <h1 className="text-3xl font-bold mb-6">Messages</h1>
      {!rows ? <Loading /> : !rows.length ? <div className="card p-10 text-center text-[#8A96A8]">No messages yet</div> : (
        <div className="space-y-3">{rows.map((m) => (
          <div key={m.id} className="card p-5" data-testid={`admin-message-${m.id}`}>
            <button className="w-full text-left flex flex-wrap items-center gap-3" onClick={() => { setOpen(open === m.id ? null : m.id); if (m.status === "new") setStatus(m, "read"); }}>
              {m.status === "new" && <span className="h-2 w-2 rounded-full bg-[#3F5E7A]" />}
              <span className="font-semibold text-[#17243A]">{m.subject}</span>
              <span className="chip">{m.inquiry_type}</span>
              {m.ref && <span className="chip font-mono" data-testid={`admin-message-ref-${m.id}`}>{m.ref}</span>}
              <span className={`chip ${EMAIL_BADGE[m.email_status] || ""}`}>email {m.email_status}</span>
              <span className="ml-auto text-xs text-[#8A96A8]">{new Date(m.created_at).toLocaleString()}</span>
            </button>
            {open === m.id && (
              <div className="mt-4 pt-4 border-t border-[#E7EDEF] text-sm space-y-3">
                <p><strong className="text-[#17243A]">{m.name}</strong> · <a href={`mailto:${m.email}`} className="text-[#3F5E7A]">{m.email}</a>{m.company && ` · ${m.company}`}</p>
                <p className="whitespace-pre-wrap">{m.message}</p>
                <div className="flex gap-2">
                  <a href={`mailto:${m.email}?subject=${encodeURIComponent(`Re: ${m.subject}${m.ref ? ` [${m.ref}]` : ""}`)}`} className="btn-primary btn-sm" data-testid={`admin-message-reply-${m.id}`}>Reply by email</a>
                  <button onClick={async () => { if (window.confirm("Delete this message permanently?")) { await call("delete", `/admin/messages/${m.id}`); reload(); } }} className="btn-ghost btn-sm text-rose-600" data-testid={`admin-message-delete-${m.id}`}><Trash2 className="h-3.5 w-3.5" />Delete</button>
                  {m.status !== "archived" && <button onClick={() => setStatus(m, "archived")} className="btn-secondary btn-sm" data-testid={`admin-message-archive-${m.id}`}>Archive</button>}
                </div>
              </div>
            )}
          </div>
        ))}</div>
      )}
    </div>
  );
}

const SETTINGS_FIELDS = [
  ["public_email", "Public business email (shown on Contact, About and footer)", "Displayed as a clickable mailto link. This does not change where form notifications go."],
  ["contact_email", "Form notification email (e.g. enquiry@saaspivora.com)", "Receives contact form notifications once the box below is ticked. Until then, notifications go to the admin email. Tick it only after the mailbox can receive mail."],
  ["ga4_id", "Google Analytics 4 measurement ID", "Format G-XXXXXXXXXX. Leave blank to disable analytics."],
  ["gsc_verification", "Google Search Console verification code", "The content value of the google-site-verification meta tag. DNS verification is also recommended for domain properties."],
  ["twitter_url", "X / Twitter URL"], ["linkedin_url", "LinkedIn URL"], ["facebook_url", "Facebook URL"], ["youtube_url", "YouTube URL"],
];

function TestEmail({ call, last, saved }) {
  const [res, setRes] = useState(last || null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try { setRes((await call("post", "/admin/settings/test-email")).data); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="card p-6 text-sm space-y-3" data-testid="settings-test-email">
      <p className="font-semibold text-[#17243A]">Test contact form routing</p>
      <p className="text-[#64728A]">Sends a test message to the saved notification email ({saved || "not set"}). "Accepted" means the email service took the message; check the inbox to confirm it arrived.</p>
      <button type="button" onClick={run} disabled={busy || !saved} className="btn-secondary btn-sm" data-testid="settings-test-email-button">{busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}Send test email</button>
      {res && (
        <p data-testid="settings-test-email-result" className={res.status === "accepted" ? "text-[#0F766E]" : "text-rose-600"}>
          Last test to {res.to}: <strong>{res.status}</strong> · {new Date(res.at).toLocaleString()} · mail servers: {res.mx?.length ? res.mx.join(", ") : "none found"}
        </p>
      )}
    </div>
  );
}

export function SiteSettings() {
  const [data, , call] = useLoad("/admin/settings");
  const [v, setV] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (data) setV(data); }, [data]);
  if (!v) return <Loading />;
  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const r = await call("put", "/admin/settings", v); setV(r.data); toast.success("Settings saved"); } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  return (
    <form onSubmit={save} className="space-y-6 max-w-2xl" data-testid="admin-settings">
      <h1 className="text-3xl font-bold">Settings</h1>
      <div className="card p-6 space-y-5">
        {SETTINGS_FIELDS.map(([k, label, hint]) => (
          <div key={k}>
            <label className="label" htmlFor={`set-${k}`}>{label}</label>
            <input id={`set-${k}`} value={v[k] || ""} onChange={(e) => setV({ ...v, [k]: e.target.value })} className="input" data-testid={`settings-${k.replace(/_/g, "-")}`} />
            {hint && <p className="mt-1 text-xs text-[#64728A]">{hint}</p>}
            {k === "contact_email" && (
              <label className="mt-3 flex items-center gap-2.5 text-sm text-[#17243A]"><input type="checkbox" checked={!!v.contact_email_active} onChange={(e) => setV({ ...v, contact_email_active: e.target.checked })} className="h-4 w-4 accent-[#0F766E]" data-testid="settings-contact-email-active" />
                Mailbox is configured — use it for notifications and show it publicly</label>
            )}
          </div>
        ))}
      </div>
      <TestEmail call={call} last={v.last_test_email} saved={data?.contact_email} />
      <div className="card p-6 text-sm space-y-1">
        <p className="font-semibold text-[#17243A]">SEO endpoints</p>
        <p>Sitemap: <a href={`${BACKEND_URL}/api/sitemap.xml`} target="_blank" rel="noreferrer" className="text-[#3F5E7A] underline">/api/sitemap.xml</a> (submit this in Search Console)</p>
        <p>robots.txt: <a href="/robots.txt" target="_blank" rel="noreferrer" className="text-[#3F5E7A] underline">/robots.txt</a></p>
      </div>
      <button disabled={busy} className="btn-primary" data-testid="settings-save">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}Save settings</button>
    </form>
  );
}

export function UsersPage() {
  const [data, reload, call] = useLoad("/users");
  const change = async (u, role) => {
    try { await call("patch", `/users/${u.user_id}/role`, { role }); toast.success("Role updated"); } catch (e) { toast.error(errMsg(e)); }
    reload();
  };
  return (
    <div data-testid="admin-users">
      <h1 className="text-3xl font-bold">Admins & users</h1>
      <p className="text-sm text-[#64728A] mt-2 mb-6">Only administrators can access this dashboard. Accounts that sign in without admin rights see a "not authorized" screen.</p>
      {!data ? <Loading /> : (
        <div className="card overflow-x-auto"><table className="w-full text-sm min-w-[480px]">
          <thead><tr className="bg-[#F1F4F6] text-left text-xs uppercase tracking-wider text-[#64728A]"><th className="p-3 pl-5">Account</th><th className="p-3">Role</th></tr></thead>
          <tbody>{data.users.map((u) => (
            <tr key={u.user_id} className="border-t border-[#E7EDEF]"><td className="p-3 pl-5 font-medium text-[#17243A]">{u.email || u.user_id}</td>
              <td className="p-3"><select value={u.role} onChange={(e) => change(u, e.target.value)} className="input !w-auto !py-1.5" data-testid={`admin-user-role-${u.user_id}`}>
                {data.roles.map((r) => <option key={r} value={r}>{r}</option>)}</select></td></tr>
          ))}</tbody>
        </table></div>
      )}
    </div>
  );
}

export function MediaLibrary() {
  const [files, reload, call] = useLoad("/admin/media");
  const copy = (u) => navigator.clipboard?.writeText(u).then(() => toast.success("URL copied"));
  const remove = async (f) => { if (window.confirm("Remove this image from the library? Pages already using it will lose the image.")) { await call("delete", `/admin/media/${f.id}`); reload(); } };
  return (
    <div data-testid="admin-media">
      <h1 className="text-3xl font-bold">Media library</h1>
      <p className="text-sm text-[#64728A] mt-2 mb-6">Images uploaded from any editor. Copy a URL to use it in a logo, cover or article.</p>
      {!files ? <Loading /> : !files.length ? <div className="card p-10 text-center text-[#8A96A8]">No uploads yet</div> : (
        <div className="grid gap-4 grid-cols-2 md:grid-cols-3 lg:grid-cols-4">{files.map((f) => (
          <div key={f.id} className="card overflow-hidden" data-testid={`admin-media-${f.id}`}>
            <img src={`${BACKEND_URL}${f.url}`} alt={f.original_filename} loading="lazy" className="h-36 w-full object-contain bg-[#F6F7F9]" />
            <div className="p-3 text-xs space-y-2">
              <p className="truncate font-medium text-[#17243A]" title={f.original_filename}>{f.original_filename}</p>
              <p className="text-[#64728A]">{Math.round((f.size || 0) / 1024)} KB · {formatDate(f.created_at)}</p>
              <div className="flex gap-1"><button onClick={() => copy(f.url)} className="btn-secondary btn-sm" data-testid={`admin-media-copy-${f.id}`}>Copy URL</button>
                <button onClick={() => remove(f)} className="btn-ghost btn-sm text-rose-600" aria-label="Remove"><Trash2 className="h-3.5 w-3.5" /></button></div>
            </div>
          </div>))}</div>
      )}
    </div>
  );
}

export function ContentSearch() {
  const call = useAdminApi();
  const [q, setQ] = useState("");
  const [rows, setRows] = useState(null);
  const go = async (e) => { e.preventDefault(); if (q.trim().length < 2) return; try { setRows((await call("get", `/admin/search?q=${encodeURIComponent(q.trim())}`)).data); } catch (err) { toast.error(errMsg(err)); } };
  return (
    <div data-testid="admin-content-search">
      <h1 className="text-3xl font-bold mb-6">Content search</h1>
      <form onSubmit={go} className="flex gap-2 max-w-xl mb-6"><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search titles and slugs across all content" className="input" data-testid="admin-search-input" />
        <button className="btn-primary" data-testid="admin-search-button">Search</button></form>
      {rows && (rows.length ? (
        <div className="card divide-y divide-[#EEF2F4]">{rows.map((r) => (
          <Link key={`${r.entity}-${r.id}`} to={`/admin/${r.entity}/${r.id}`} className="flex items-center gap-3 px-5 py-3 hover:bg-[#F6F7F9]" data-testid={`admin-search-result-${r.slug}`}>
            <span className="chip">{r.entity}</span><span className="font-medium text-[#17243A]">{r.title}</span>{r.status && <span className="ml-auto chip">{r.status}</span>}</Link>))}</div>
      ) : <p className="text-sm text-[#64728A]">No matches.</p>)}
    </div>
  );
}
